import {useEffect, useState} from 'react';
import {DisplayMessage, PersonBookingResponseDto, PersonResponseDto} from '../lib/interfaces';
import {useBookingState} from '../store';
import {t} from 'i18next';
import {convertDateToString, formatDateToReadableString} from '../helpers';
import {useNavigate} from 'react-router-dom';
import {DisplayMessageTypes, PersonBookingStatus, RoutePath} from '../lib/enums';
import CancelBookingButton from '../components/CancelBookingButton';
import {CUSTOM_OCCASION_VALUE, RebookThresholdOffsetDays, UPPSALA_KOMMUN_PHONE_NUMBER} from '../utils/constants';
import {Trans} from 'react-i18next';
import {getMyBookings} from '../services/bookingServices';
import {UaAccordion, UaButton, UaDialog, UaDisplayMessage, UaSpinner} from 'uppsala.ui.react';

const MyBookingPage = () => {
  const state = useBookingState();
  const navigate = useNavigate();
  const personalIdentityNumber = useBookingState((state) => state.person1.personalIdentityNumber);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PersonBookingResponseDto[] | null>(null);
  const [noBookingFound, setNoBookingFound] = useState(false);
  const [cancelationMessage, setCancelationMessage] = useState<{displayMessage: DisplayMessage; bookingId: string}>();
  const [dialogMessage, setDialogMessage] = useState<any>(null);

  useEffect(() => {
    fetchMyBookings();
  }, []);

  const fetchMyBookings = async () => {
    if (loading) return;
    setLoading(true);

    if (!personalIdentityNumber) {
      setLoading(false);
      alert('Inget personnummer fanns kopplat till din inloggning.');
      navigate(-1);
      return;
    }

    const result = await getMyBookings(personalIdentityNumber);
    if (!result.data) {
      setNoBookingFound(true);
      setLoading(false);
      return;
    }

    setData(result.data);
    setLoading(false);
  };

  const sendToRebookingPage = (bookingId: string, weddingOccasionDate: string, isCustomBooking: boolean) => {
    if (isCustomBooking) {
      setDialogMessage({
        type: DisplayMessageTypes.Info,
        title: t('booking.header.rebookCustomOccasion'),
        content: (
          <p className="whitespace-pre-line">
            <Trans
              i18nKey="booking.rebookCustomOccasionInfo"
              values={{number: UPPSALA_KOMMUN_PHONE_NUMBER}}
              components={{
                1: <a href={`tel:${UPPSALA_KOMMUN_PHONE_NUMBER}`} aria-label="Telefonnummer" />,
              }}
            />
          </p>
        ),
      });
      window.scrollTo(0, 0); // Scroll to top of the page
      return;
    }

    const isActiveBooking = new Date(weddingOccasionDate) > new Date();
    if (!isActiveBooking) {
      setDialogMessage({
        title: t('booking.header.rebookOccasion'),
        type: DisplayMessageTypes.Info,
        content: (
          <p className="whitespace-pre-line">
            <Trans
              i18nKey="apiResponse.BOOKING_NOT_ACTIVE"
              values={{number: UPPSALA_KOMMUN_PHONE_NUMBER}}
              components={{
                1: <a href={`tel:${UPPSALA_KOMMUN_PHONE_NUMBER}`} aria-label="Telefonnummer" />,
              }}
            />
          </p>
        ),
      });
      window.scrollTo(0, 0); // Scroll to top of the page
      return;
    }

    // Clear states before sending to rebooking page
    state.setSelectedMonthYear('');
    state.setWeddingOccasionId('');
    state.setWeddingOccasion(null);
    navigate(RoutePath.RebookBookingPage.replace(':bookingId', bookingId));
  };

  const renderContent = (item: PersonBookingResponseDto, idx: number, renderEditButtons?: boolean) => {
    const isCustomBooking = item.location.name === CUSTOM_OCCASION_VALUE;
    return (
      <div key={idx} style={{marginBottom: '50px'}}>
        <div style={{marginBottom: '15px'}}>
          <span style={{marginTop: '30px', fontWeight: 'bold'}}>{t('booking.time')}</span>
          <p>{formatDateToReadableString(new Date(item.dateTime))}</p>
        </div>

        <div style={{marginBottom: '15px'}}>
          <span style={{fontWeight: 'bold'}}>{t('booking.location')}</span>
          {!isCustomBooking && <p>{item.location.name}</p>}
          <p>{item.location.address}</p>
          <p>{item.location.city}</p>
        </div>

        <div style={{marginBottom: '15px'}}>
          <span style={{fontWeight: 'bold'}}>{t('booking.weddingOfficiant')}</span>
          <p>{item.officiantName}</p>
        </div>

        <div style={{marginBottom: '15px'}}>
          <div style={{marginBottom: '15px'}}>
            <span style={{fontWeight: 'bold'}}>Person 1</span>
            {renderPersonData(item.person1)}
          </div>

          <div style={{marginBottom: '15px'}}>
            <span style={{fontWeight: 'bold'}}>Person 2</span>
            {renderPersonData(item.person2)}
          </div>
        </div>

        {cancelationMessage?.displayMessage && cancelationMessage.bookingId === item.bookingId ? (
          <UaDisplayMessage
            style={{margin: '25px 0'}}
            type={cancelationMessage.displayMessage.type as DisplayMessageTypes}
            message={cancelationMessage.displayMessage.message}
            onClose={() => setCancelationMessage(undefined)}
          />
        ) : null}

        {renderEditButtons && (
          <div className="flex-row" style={{gap: '1rem', flexWrap: 'wrap-reverse'}}>
            <CancelBookingButton
              bookingId={item.bookingId ?? ''}
              setDisplayMessage={(displayMessage, bookingId) => {
                if (displayMessage.type === DisplayMessageTypes.Success) {
                  fetchMyBookings();
                } else {
                  setCancelationMessage({displayMessage, bookingId});
                }
              }}
              onClick={() => window.scrollTo(0, 0)}
            />
            <UaButton
              title={t('button.changeTime')}
              type="primary"
              onClick={() => {
                const itemDate = new Date(item.dateTime);
                const currentDate = new Date();
                const rebookThresholdOffsetDays = RebookThresholdOffsetDays; // Assuming RebookThresholdOffsetDays is a number
                const tooShortNoticeToRebook =
                  (itemDate.getTime() - currentDate.getTime()) / (1000 * 3600 * 24) < rebookThresholdOffsetDays;

                if (tooShortNoticeToRebook) {
                  setDialogMessage({
                    title: t('Det går ej att boka om tiden'),
                    type: DisplayMessageTypes.Warning,
                    content: (
                      <p className="whitespace-pre-line">
                        <Trans
                          i18nKey={'apiResponse.REBOOK_BELOW_THRESHOLD' as string}
                          values={{number: UPPSALA_KOMMUN_PHONE_NUMBER}}
                          components={{
                            1: <a href={`tel:${UPPSALA_KOMMUN_PHONE_NUMBER}`} aria-label="Telefonnummer" />,
                          }}
                        />
                      </p>
                    ),
                  });
                } else {
                  sendToRebookingPage(item.bookingId, item.dateTime, isCustomBooking);
                }
              }}
            />
          </div>
        )}
      </div>
    );

    function renderPersonData(person: PersonResponseDto) {
      return (
        <div>
          {person.personalIdentityNumber && <p>{person.personalIdentityNumber}</p>}
          <p>{person.email}</p>
          <p>{person.phoneNumber}</p>

          {person.extended && (
            <div>
              <p>{person.extended.firstName ?? ''}</p>
              <p>{person.extended.lastName ?? ''}</p>
              <p>{`${person.extended.address ?? ''} ${person.extended.zipCode ?? ''} ${person.extended.city ?? ''}`}</p>
              <p>{person.extended.country ?? ''}</p>
              <p>{convertDateToString(person.extended.dateOfBirth, 'YYYY-MM-DD')}</p>
            </div>
          )}
        </div>
      );
    }
  };

  const retrieveBookings = (type: PersonBookingStatus) => {
    if (!data) return [];
    switch (type) {
      case PersonBookingStatus.Booked:
        return data?.filter(
          (item) =>
            [PersonBookingStatus.Booked, PersonBookingStatus.Opened].includes(item.status) &&
            new Date(item.dateTime) >= new Date()
        );
      case PersonBookingStatus.Closed:
        return data?.filter(
          (item) =>
            item.status === PersonBookingStatus.Closed ||
            (item.status !== PersonBookingStatus.Canceled && new Date(item.dateTime) < new Date())
        );
      case PersonBookingStatus.Canceled:
      default:
        return data?.filter((item) => item.status === type);
    }
  };

  if (noBookingFound)
    return (
      <div>
        <h4>{t('header.noBookingFound')}</h4>
        <p>{t('booking.noBookingFoundInfo')}</p>
        <UaButton
          style={{margin: '50px 0'}}
          title={t('button.seeAvailableBookings')}
          onClick={() => navigate(RoutePath.HomePage)}
        />
      </div>
    );

  if (loading)
    return (
      <>
        <h1>{t('header.myBooking')}</h1>
        <div style={{margin: '50px 25px'}}>
          <UaSpinner />
        </div>
      </>
    );

  return (
    <div style={{maxWidth: '800px'}}>
      <h1 style={{marginBottom: '15px'}}>{t('booking.header.myBookings')}</h1>

      <UaAccordion
        title={t('booking.header.booked')}
        content={
          retrieveBookings(PersonBookingStatus.Booked).length === 0 ? (
            <p>{t('booking.noBookedBookingFound')}</p>
          ) : (
            retrieveBookings(PersonBookingStatus.Booked).map((item, idx) => renderContent(item, idx, true))
          )
        }
        defaultOpen={true}
      />

      <UaAccordion
        title={t('booking.header.closed')}
        content={
          retrieveBookings(PersonBookingStatus.Closed).length === 0 ? (
            <p>{t('booking.noClosedBookingFound')}</p>
          ) : (
            retrieveBookings(PersonBookingStatus.Closed).map((item, idx) => renderContent(item, idx))
          )
        }
      />

      <UaAccordion
        title={t('booking.header.canceled')}
        content={
          retrieveBookings(PersonBookingStatus.Canceled).length === 0 ? (
            <p>{t('booking.noCanceledBookingFound')}</p>
          ) : (
            retrieveBookings(PersonBookingStatus.Canceled).map((item, idx) => renderContent(item, idx))
          )
        }
      />

      <div style={{marginBottom: '30px'}} />

      <UaDialog
        visible={dialogMessage !== null}
        onCancel={() => setDialogMessage(null)}
        content={dialogMessage?.content}
        title={dialogMessage?.title}
      />
    </div>
  );
};

export default MyBookingPage;
